import { Folder, AlertCircle, Loader2, Play } from 'lucide-react';
import { Resource } from './types';
import { cn } from '@/lib/utils';

interface Props {
  resources: Resource[];
  loading: boolean;
  selectedPath: string;
  jobActive: boolean;
  onSelect: (relativePath: string) => void;
  onDrillIn: (resource: Resource) => void;
  onQuickOptimize: (relativePath: string) => void;
}

export default function FolderListView({
  resources, loading, selectedPath, jobActive,
  onSelect, onDrillIn, onQuickOptimize,
}: Props) {
  return (
    <div className="rounded-2xl border border-white/[0.05] flex flex-col" style={{ background: '#11141c' }}>
      <div className="flex items-center justify-between border-b border-white/[0.04] px-5 py-3">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white">
          <Folder size={13} className="text-[#00ffd1]" />
          Stream Resource Directories
        </div>
        <span className="text-[9px] font-bold uppercase bg-white/[0.04] px-2 py-0.5 rounded text-slate-500">
          {resources.length} Folders
        </span>
      </div>

      <div className="overflow-y-auto p-4" style={{ maxHeight: 380 }}>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3 text-slate-400">
            <Loader2 size={22} className="animate-spin text-[#00ffd1]" />
            <span className="text-xs">Scanning FiveM folders...</span>
          </div>
        ) : resources.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-xs uppercase font-semibold">
            No folders containing a "stream/" directory were found.
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            {resources.map(folder => {
              const isSelected = selectedPath === folder.relativePath;
              return (
                <div
                  key={folder.relativePath}
                  onClick={() => !jobActive && onSelect(folder.relativePath)}
                  onDoubleClick={() => !jobActive && onDrillIn(folder)}
                  className={cn(
                    'flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-150 gap-3 border select-none',
                    isSelected
                      ? 'border-[#00ffd1]/30 bg-[#00ffd1]/[0.03]'
                      : 'border-white/[0.03] bg-white/[0.01] hover:bg-white/[0.03] hover:border-white/[0.06]',
                    jobActive && 'opacity-50 pointer-events-none',
                  )}
                >
                  <div className="flex items-start gap-3">
                    <Folder
                      size={15}
                      className={cn('shrink-0 mt-0.5', folder.hasOversized ? 'text-amber-500' : 'text-slate-500')}
                    />
                    <div>
                      <div className="text-[11px] font-bold text-white">{folder.name}</div>
                      <div className="text-[9px] font-mono text-slate-500 mt-0.5">{folder.relativePath}</div>
                      {folder.hasOversized && (
                        <div className="text-[8.5px] font-semibold text-amber-500 mt-1 flex items-center gap-1 uppercase">
                          <AlertCircle size={9} />
                          {folder.oversizedFiles.length} oversized:{' '}
                          {folder.oversizedFiles.map(f => `${f.name} (${f.sizeMb}MB)`).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <div className="text-[10px] font-semibold text-slate-300">{folder.fileCount} .YTD files</div>
                      <div className="text-[9px] text-slate-500">{folder.sizeMb} MB</div>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); onQuickOptimize(folder.relativePath); }}
                      disabled={jobActive}
                      className="px-2.5 py-1 bg-white/[0.05] hover:bg-[#00ffd1] hover:text-slate-950 font-bold text-[8.5px] rounded-lg transition-all duration-150 uppercase border border-white/[0.05] disabled:opacity-40"
                    >
                      Quick Optimize
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!loading && resources.length > 0 && (
        <div className="flex items-center justify-center gap-1.5 py-2.5 border-t border-white/[0.03] text-[9px] text-slate-700 font-semibold">
          <Play size={9} />
          Double-click any folder to browse its YTD files
        </div>
      )}
    </div>
  );
}
